import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import EngineeringIcon from "./EngineeringIcons";
import AnimatedDivider from "./AnimatedDivider";

interface ProjectDetail {
  id: string;
  title: string;
  category: string;
  status: "completed" | "ongoing" | "planned";
  description: string;
  longDescription: string;
  client: string;
  location: string;
  startDate: string;
  endDate?: string;
  budget: string;
  team: string[];
  technologies: string[];
  challenges: string[];
  solutions: string[];
  outcomes: string[];
  images: string[];
  specifications: {
    title: string;
    value: string;
    unit?: string;
  }[];
  milestones: {
    date: string;
    title: string;
    description: string;
    completed: boolean;
  }[];
  relatedProjects: {
    id: string;
    title: string;
    category: string;
    image: string;
    status: "completed" | "ongoing" | "planned";
  }[];
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Mock project data - in a real app, this would come from an API
  const mockProjects: ProjectDetail[] = [
    {
      id: "1",
      title: "Automated Palm Oil Processing Plant",
      category: "Farm Machinery & Food Processing",
      status: "completed",
      description: "Complete automation of palm oil processing with advanced machinery and quality control systems.",
      longDescription: "This comprehensive project involved the design, fabrication, and installation of a fully automated palm oil processing plant. The system includes advanced harvesting equipment, automated processing lines, quality control systems, and sustainable waste management solutions. The plant processes 50 tons of palm fruit per day with 95% efficiency.",
      client: "Ekumfi Palm Oil Cooperative",
      location: "Ekumfi Essuehyia, Central Region, Ghana",
      startDate: "2023-01-15",
      endDate: "2023-08-30",
      budget: "$2.5M",
      team: ["Lead Engineer: Kwame Asante", "Mechanical Engineer: Sarah Mensah", "Electrical Engineer: David Osei", "Quality Control: Grace Addo"],
      technologies: ["Automated Processing Lines", "Quality Control Systems", "Waste Management", "Energy Recovery", "IoT Monitoring"],
      challenges: [
        "Complex automation requirements for varying palm fruit quality",
        "Integration of multiple processing systems",
        "Energy efficiency optimization",
        "Waste management and environmental compliance"
      ],
      solutions: [
        "Implemented AI-powered quality assessment system",
        "Designed modular processing units for easy maintenance",
        "Integrated solar power with energy recovery systems",
        "Developed zero-waste processing methodology"
      ],
      outcomes: [
        "50% increase in processing efficiency",
        "95% reduction in waste generation",
        "40% reduction in energy consumption",
        "Created 25 new jobs in the community"
      ],
      images: [
        "/palm_oil_mill.png",
        "/palm_kenel_oil_machines.png",
        "/palm_threshing_machine.png",
        "/project1.png"
      ],
      specifications: [
        { title: "Processing Capacity", value: "50", unit: "tons/day" },
        { title: "Efficiency Rate", value: "95", unit: "%" },
        { title: "Energy Consumption", value: "40", unit: "% reduction" },
        { title: "Waste Reduction", value: "95", unit: "%" },
        { title: "Automation Level", value: "90", unit: "%" },
        { title: "Quality Control Points", value: "15", unit: "points" }
      ],
      milestones: [
        { date: "2023-01-15", title: "Project Initiation", description: "Project planning and team assembly", completed: true },
        { date: "2023-03-20", title: "Design Phase Complete", description: "Engineering designs and specifications finalized", completed: true },
        { date: "2023-05-10", title: "Fabrication Complete", description: "All machinery fabricated and tested", completed: true },
        { date: "2023-07-15", title: "Installation Complete", description: "Plant installation and system integration", completed: true },
        { date: "2023-08-30", title: "Commissioning", description: "Final testing and handover to client", completed: true }
      ],
      relatedProjects: [
        { id: "2", title: "Solar-Powered Irrigation System", category: "Energy Engineering", image: "/project2.png", status: "ongoing" },
        { id: "3", title: "Interlocking Brick Factory", category: "Sustainable Building", image: "/project3.png", status: "completed" },
        { id: "4", title: "Fishing Boat Fleet", category: "Marine Engineering", image: "/project4.png", status: "planned" }
      ]
    },
    {
      id: "2",
      title: "Solar-Powered Irrigation System",
      category: "Energy Engineering",
      status: "ongoing",
      description: "Large-scale solar-powered irrigation system for agricultural development.",
      longDescription: "This innovative project combines renewable energy technology with agricultural automation. The system uses solar panels to power advanced irrigation systems, providing sustainable water management for large-scale farming operations. The project includes smart sensors, automated controls, and mobile monitoring capabilities.",
      client: "Ekumfi Agricultural Development Authority",
      location: "Ekumfi District, Central Region, Ghana",
      startDate: "2023-09-01",
      budget: "GHS12,000",
      team: ["Lead Engineer: Michael Kofi", "Solar Engineer: Abena Osei", "Agricultural Engineer: Kwesi Addo", "Software Engineer: Nana Yaa"],
      technologies: ["Solar Power Systems", "Smart Irrigation", "IoT Sensors", "Mobile Monitoring", "Automated Controls"],
      challenges: [
        "Large-scale solar system integration",
        "Water distribution optimization",
        "Weather-based automation",
        "Remote monitoring and control"
      ],
      solutions: [
        "Implemented modular solar power system",
        "Designed intelligent water distribution network",
        "Developed weather-responsive automation",
        "Created mobile monitoring application"
      ],
      outcomes: [
        "80% reduction in water consumption",
        "100% renewable energy powered",
        "Real-time monitoring capabilities",
        "Improved crop yields by 60%"
      ],
      images: [
        "/project2.png",
        "/project5.png",
        "/project6.png"
      ],
      specifications: [
        { title: "Solar Capacity", value: "500", unit: "kW" },
        { title: "Irrigation Coverage", value: "200", unit: "hectares" },
        { title: "Water Savings", value: "80", unit: "%" },
        { title: "Energy Independence", value: "100", unit: "%" },
        { title: "Automation Level", value: "95", unit: "%" }
      ],
      milestones: [
        { date: "2023-09-01", title: "Project Initiation", description: "Project planning and site assessment", completed: true },
        { date: "2023-11-15", title: "Solar Installation", description: "Solar panel installation and testing", completed: true },
        { date: "2024-01-20", title: "Irrigation System", description: "Irrigation infrastructure installation", completed: true },
        { date: "2024-03-15", title: "Automation Integration", description: "Smart controls and monitoring systems", completed: false },
        { date: "2024-05-30", title: "Final Testing", description: "System testing and optimization", completed: false }
      ],
      relatedProjects: [
        { id: "1", title: "Automated Palm Oil Processing Plant", category: "Farm Machinery & Food Processing", image: "/palm_oil_mill.png", status: "completed" },
        { id: "3", title: "Interlocking Brick Factory", category: "Sustainable Building", image: "/project3.png", status: "completed" },
        { id: "5", title: "Wood Processing Facility", category: "Wood Works Engineering", image: "/project5.png", status: "ongoing" }
      ]
    },
    {
      id: "palm-threshing-machine-installation",
      title: "Palm Threshing Machine Installation",
      category: "Farm Machinery & Food Processing",
      status: "completed",
      description: "High-efficiency palm fruit threshing machine designed for optimal fruit separation and processing.",
      longDescription: "This project involved the design, fabrication, and installation of a state-of-the-art palm fruit threshing machine. The system is designed to efficiently separate palm fruits from their bunches while maintaining fruit integrity and maximizing oil extraction potential. The machine features advanced automation, quality control systems, and energy-efficient operation.",
      client: "Ekumfi Palm Oil Cooperative",
      location: "Ekumfi Essuehyia, Central Region, Ghana",
      startDate: "2023-03-01",
      endDate: "2023-06-15",
      budget: "GHS9,000",
      team: ["Lead Engineer: Kwame Asante", "Mechanical Engineer: Sarah Mensah", "Electrical Engineer: David Osei", "Quality Control: Grace Addo"],
      technologies: ["Automated Threshing Systems", "Quality Control Sensors", "Energy Management", "IoT Monitoring", "Safety Systems"],
      challenges: [
        "Maintaining fruit integrity during threshing process",
        "Optimizing threshing speed for different fruit sizes",
        "Reducing energy consumption while maintaining efficiency",
        "Ensuring consistent quality output"
      ],
      solutions: [
        "Implemented variable speed control system",
        "Designed gentle threshing mechanism with adjustable parameters",
        "Integrated energy recovery and management systems",
        "Developed real-time quality monitoring and feedback system"
      ],
      outcomes: [
        "40% increase in threshing efficiency",
        "95% fruit integrity preservation",
        "30% reduction in energy consumption",
        "Improved oil extraction yield by 15%"
      ],
      images: [
        "/palm_threshing_machine.png",
        "/palm_oil_mill.png",
        "/project1.png",
        "/project2.png"
      ],
      specifications: [
        { title: "Threshing Capacity", value: "2", unit: "tons/hour" },
        { title: "Fruit Integrity", value: "95", unit: "%" },
        { title: "Energy Efficiency", value: "30", unit: "% improvement" },
        { title: "Oil Yield Improvement", value: "15", unit: "%" },
        { title: "Automation Level", value: "85", unit: "%" },
        { title: "Safety Features", value: "12", unit: "systems" }
      ],
      milestones: [
        { date: "2023-03-01", title: "Project Initiation", description: "Requirements analysis and design planning", completed: true },
        { date: "2023-04-15", title: "Design Complete", description: "Engineering design and specifications finalized", completed: true },
        { date: "2023-05-20", title: "Fabrication Complete", description: "Machine fabrication and component testing", completed: true },
        { date: "2023-06-01", title: "Installation", description: "Machine installation and system integration", completed: true },
        { date: "2023-06-15", title: "Commissioning", description: "Testing, calibration, and handover", completed: true }
      ],
      relatedProjects: [
        { id: "1", title: "Automated Palm Oil Processing Plant", category: "Farm Machinery & Food Processing", image: "/palm_oil_mill.png", status: "completed" },
        { id: "palm-kernel-oil-extraction", title: "Palm Kernel Oil Extraction System", category: "Farm Machinery & Food Processing", image: "/palm_kenel_oil_machines.png", status: "completed" },
        { id: "industrial-drying-system", title: "Industrial Drying System", category: "Farm Machinery & Food Processing", image: "/drying_machine.png", status: "ongoing" }
      ]
    },
    {
      id: "palm-kernel-oil-extraction",
      title: "Palm Kernel Oil Extraction System",
      category: "Farm Machinery & Food Processing",
      status: "completed",
      description: "Advanced palm kernel oil extraction machine with high yield rate and automated processing.",
      longDescription: "This comprehensive project delivered a complete palm kernel oil extraction system capable of processing large volumes of palm kernels with maximum oil yield. The system includes automated feeding, crushing, pressing, and filtering stages, all integrated with quality control and monitoring systems.",
      client: "Ekumfi Palm Oil Cooperative",
      location: "Ekumfi Essuehyia, Central Region, Ghana",
      startDate: "2023-07-01",
      endDate: "2023-10-30",
      budget: "GHS10,000",
      team: ["Lead Engineer: Michael Kofi", "Mechanical Engineer: Abena Osei", "Chemical Engineer: Kwesi Addo", "Quality Control: Nana Yaa"],
      technologies: ["Automated Extraction Systems", "Quality Control", "Energy Management", "IoT Monitoring", "Filtration Systems"],
      challenges: [
        "Maximizing oil extraction yield from palm kernels",
        "Maintaining oil quality throughout the process",
        "Automating the entire extraction workflow",
        "Ensuring food safety standards compliance"
      ],
      solutions: [
        "Implemented multi-stage extraction process",
        "Designed automated quality control systems",
        "Integrated real-time monitoring and adjustment",
        "Developed comprehensive food safety protocols"
      ],
      outcomes: [
        "25% increase in oil extraction yield",
        "99.5% oil purity achieved",
        "50% reduction in processing time",
        "Full automation achieved"
      ],
      images: [
        "/palm_kenel_oil_machines.png",
        "/palm_oil_mill.png",
        "/project3.png",
        "/project4.png"
      ],
      specifications: [
        { title: "Extraction Capacity", value: "500", unit: "L/day" },
        { title: "Oil Yield", value: "25", unit: "% improvement" },
        { title: "Oil Purity", value: "99.5", unit: "%" },
        { title: "Processing Time", value: "50", unit: "% reduction" },
        { title: "Automation Level", value: "100", unit: "%" }
      ],
      milestones: [
        { date: "2023-07-01", title: "Project Initiation", description: "System design and planning", completed: true },
        { date: "2023-08-15", title: "Design Complete", description: "Engineering design finalized", completed: true },
        { date: "2023-09-30", title: "Fabrication Complete", description: "System fabrication and testing", completed: true },
        { date: "2023-10-15", title: "Installation", description: "System installation and integration", completed: true },
        { date: "2023-10-30", title: "Commissioning", description: "Testing and handover", completed: true }
      ],
      relatedProjects: [
        { id: "palm-threshing-machine-installation", title: "Palm Threshing Machine Installation", category: "Farm Machinery & Food Processing", image: "/palm_threshing_machine.png", status: "completed" },
        { id: "1", title: "Automated Palm Oil Processing Plant", category: "Farm Machinery & Food Processing", image: "/palm_oil_mill.png", status: "completed" },
        { id: "industrial-drying-system", title: "Industrial Drying System", category: "Farm Machinery & Food Processing", image: "/drying_machine.png", status: "ongoing" }
      ]
    },
    {
      id: "industrial-drying-system",
      title: "Industrial Drying System",
      category: "Farm Machinery & Food Processing",
      status: "ongoing",
      description: "Industrial-grade drying machine for agricultural products processing with energy-efficient operation.",
      longDescription: "This project involves the development of a large-scale industrial drying system designed for processing various agricultural products. The system features advanced temperature control, multiple drying chambers, energy recovery systems, and digital monitoring capabilities for optimal drying conditions.",
      client: "Ekumfi Agricultural Processing Center",
      location: "Ekumfi District, Central Region, Ghana",
      startDate: "2024-01-15",
      budget: "GHS11,000",
      team: ["Lead Engineer: Sarah Mensah", "Thermal Engineer: David Osei", "Control Engineer: Grace Addo", "Energy Engineer: Kwame Asante"],
      technologies: ["Thermal Management", "Energy Recovery", "Digital Controls", "IoT Monitoring", "Multi-Chamber Design"],
      challenges: [
        "Maintaining consistent temperature across multiple chambers",
        "Optimizing energy consumption for large-scale operations",
        "Ensuring uniform drying across all products",
        "Integrating energy recovery systems"
      ],
      solutions: [
        "Implemented advanced thermal management system",
        "Designed energy recovery and heat exchange systems",
        "Developed intelligent control algorithms",
        "Integrated real-time monitoring and adjustment"
      ],
      outcomes: [
        "60% reduction in energy consumption",
        "Improved drying uniformity by 40%",
        "Reduced processing time by 30%",
        "Enhanced product quality consistency"
      ],
      images: [
        "/drying_machine.png",
        "/project5.png",
        "/project6.png"
      ],
      specifications: [
        { title: "Drying Capacity", value: "5", unit: "tons/day" },
        { title: "Energy Efficiency", value: "60", unit: "% improvement" },
        { title: "Temperature Control", value: "±2", unit: "°C accuracy" },
        { title: "Chambers", value: "6", unit: "units" },
        { title: "Automation Level", value: "90", unit: "%" }
      ],
      milestones: [
        { date: "2024-01-15", title: "Project Initiation", description: "Requirements analysis and design", completed: true },
        { date: "2024-02-28", title: "Design Complete", description: "Engineering design finalized", completed: true },
        { date: "2024-04-15", title: "Fabrication", description: "System fabrication in progress", completed: false },
        { date: "2024-06-30", title: "Installation", description: "System installation and testing", completed: false },
        { date: "2024-08-15", title: "Commissioning", description: "Final testing and handover", completed: false }
      ],
      relatedProjects: [
        { id: "palm-threshing-machine-installation", title: "Palm Threshing Machine Installation", category: "Farm Machinery & Food Processing", image: "/palm_threshing_machine.png", status: "completed" },
        { id: "palm-kernel-oil-extraction", title: "Palm Kernel Oil Extraction System", category: "Farm Machinery & Food Processing", image: "/palm_kenel_oil_machines.png", status: "completed" },
        { id: "pasteurizer-unit", title: "Pasteurizer Unit", category: "Farm Machinery & Food Processing", image: "/pasturizer.png", status: "planned" }
      ]
    },
    {
      id: "pasteurizer-unit",
      title: "Pasteurizer Unit",
      category: "Farm Machinery & Food Processing",
      status: "planned",
      description: "High-quality pasteurizer unit for food and beverage processing with advanced temperature control.",
      longDescription: "This planned project will deliver a state-of-the-art pasteurization system designed for food and beverage processing. The unit will feature precise temperature control, multiple pasteurization chambers, energy recovery systems, and digital control interfaces for optimal food safety and quality.",
      client: "Ekumfi Food Processing Cooperative",
      location: "Ekumfi Essuehyia, Central Region, Ghana",
      startDate: "2024-09-01",
      budget: "GHS12,000",
      team: ["Lead Engineer: Michael Kofi", "Food Engineer: Abena Osei", "Control Engineer: Kwesi Addo", "Safety Engineer: Nana Yaa"],
      technologies: ["Precision Temperature Control", "Food Safety Systems", "Energy Recovery", "Digital Controls", "Quality Monitoring"],
      challenges: [
        "Ensuring precise temperature control for food safety",
        "Meeting international food safety standards",
        "Optimizing energy consumption",
        "Integrating quality monitoring systems"
      ],
      solutions: [
        "Design precision temperature control systems",
        "Implement comprehensive food safety protocols",
        "Develop energy-efficient operation modes",
        "Integrate real-time quality monitoring"
      ],
      outcomes: [
        "Enhanced food safety compliance",
        "Improved product shelf life",
        "Reduced energy consumption",
        "Increased processing capacity"
      ],
      images: [
        "/pasturizer.png",
        "/project1.png",
        "/project2.png"
      ],
      specifications: [
        { title: "Processing Capacity", value: "2000", unit: "L/day" },
        { title: "Temperature Accuracy", value: "±0.5", unit: "°C" },
        { title: "Energy Efficiency", value: "40", unit: "% improvement" },
        { title: "Safety Standards", value: "ISO 22000", unit: "compliant" },
        { title: "Chambers", value: "4", unit: "units" }
      ],
      milestones: [
        { date: "2024-09-01", title: "Project Initiation", description: "Planning and design phase", completed: false },
        { date: "2024-10-15", title: "Design Complete", description: "Engineering design finalized", completed: false },
        { date: "2024-12-01", title: "Fabrication", description: "System fabrication", completed: false },
        { date: "2025-02-15", title: "Installation", description: "System installation", completed: false },
        { date: "2025-03-30", title: "Commissioning", description: "Testing and handover", completed: false }
      ],
      relatedProjects: [
        { id: "industrial-drying-system", title: "Industrial Drying System", category: "Farm Machinery & Food Processing", image: "/drying_machine.png", status: "ongoing" },
        { id: "palm-kernel-oil-extraction", title: "Palm Kernel Oil Extraction System", category: "Farm Machinery & Food Processing", image: "/palm_kenel_oil_machines.png", status: "completed" },
        { id: "automated-packaging-line", title: "Automated Packaging Line", category: "Farm Machinery & Food Processing", image: "/project3.png", status: "planned" }
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      const foundProject = mockProjects.find(p => p.id === id);
      setProject(foundProject || null);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500";
      case "ongoing": return "bg-blue-500";
      case "planned": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed": return "Completed";
      case "ongoing": return "In Progress";
      case "planned": return "Planned";
      default: return "Unknown";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <EngineeringIcon type="gear" size="lg" className="text-gray-400 mx-auto mb-4" animated={false} />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Project Not Found</h2>
          <p className="text-gray-600 mb-4">The project you're looking for doesn't exist.</p>
          <Link to="/projects" className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors">
            View All Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Link to="/projects" className="text-orange-200 hover:text-white transition-colors flex items-center gap-2">
                <EngineeringIcon type="gear" size="sm" animated={false} />
                Back to Projects
              </Link>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(project.status)}`}>
                    {getStatusText(project.status)}
                  </span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {project.category}
                  </span>
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold mb-6">{project.title}</h1>
                <p className="text-xl mb-6">{project.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-orange-200">Client:</span>
                    <p className="font-semibold">{project.client}</p>
                  </div>
                  <div>
                    <span className="text-orange-200">Location:</span>
                    <p className="font-semibold">{project.location}</p>
                  </div>
                  <div>
                    <span className="text-orange-200">Budget:</span>
                    <p className="font-semibold">{project.budget}</p>
                  </div>
                  <div>
                    <span className="text-orange-200">Duration:</span>
                    <p className="font-semibold">
                      {project.startDate} - {project.endDate || "Ongoing"}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                {project.images.length > 0 && (
                  <div className="relative h-80 rounded-lg overflow-hidden">
                    <img
                      src={project.images[activeImage]}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                    {project.images.length > 1 && (
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                        {project.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveImage(index)}
                            className={`w-3 h-3 rounded-full transition-colors ${
                              index === activeImage ? "bg-white" : "bg-white/50"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Project Details */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Description */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <EngineeringIcon type="bolt" size="md" className="text-orange-500" />
                    Project Overview
                  </h2>
                  <p className="text-gray-700 leading-relaxed">{project.longDescription}</p>
                </div>

                {/* Team */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <EngineeringIcon type="cog" size="md" className="text-orange-500" />
                    Project Team
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.team.map((member, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {member.split(":")[1]?.charAt(0) || "T"}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{member.split(":")[1]?.trim() || member}</p>
                          <p className="text-sm text-gray-600">{member.split(":")[0]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <EngineeringIcon type="wrench" size="md" className="text-orange-500" />
                    Technologies Used
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Challenges & Solutions */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <EngineeringIcon type="hammer" size="md" className="text-orange-500" />
                    Challenges & Solutions
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-red-600">Challenges</h3>
                      <ul className="space-y-2">
                        {project.challenges.map((challenge, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <EngineeringIcon type="bolt" size="sm" className="text-red-500 mt-1" animated={false} />
                            <span className="text-gray-700">{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-green-600">Solutions</h3>
                      <ul className="space-y-2">
                        {project.solutions.map((solution, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <EngineeringIcon type="gear" size="sm" className="text-green-500 mt-1" animated={false} />
                            <span className="text-gray-700">{solution}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Outcomes */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <EngineeringIcon type="chain" size="md" className="text-orange-500" />
                    Project Outcomes
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                        <EngineeringIcon type="bolt" size="sm" className="text-green-500 mt-1" animated={false} />
                        <span className="text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Specifications */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <EngineeringIcon type="screw" size="sm" className="text-orange-500" />
                    Specifications
                  </h3>
                  <div className="space-y-3">
                    {project.specifications.map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">{spec.title}</span>
                        <span className="font-semibold text-gray-800">
                          {spec.value}{spec.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestones */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <EngineeringIcon type="pulley" size="sm" className="text-orange-500" />
                    Project Milestones
                  </h3>
                  <div className="space-y-4">
                    {project.milestones.map((milestone, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-2 ${
                          milestone.completed ? "bg-green-500" : "bg-gray-300"
                        }`}></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-800">{milestone.title}</span>
                                                         {milestone.completed && (
                               <EngineeringIcon type="bolt" size="sm" className="text-green-500" animated={false} />
                             )}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">{milestone.description}</p>
                          <p className="text-xs text-gray-500">{milestone.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Related Projects */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <EngineeringIcon type="gear" size="sm" className="text-orange-500" />
                    Related Projects
                  </h3>
                  <div className="space-y-4">
                    {project.relatedProjects.map((related) => (
                      <Link
                        key={related.id}
                        to={`/projects/${related.id}`}
                        className="block group"
                      >
                        <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 group-hover:text-orange-500 transition-colors">
                              {related.title}
                            </h4>
                            <p className="text-sm text-gray-600">{related.category}</p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              related.status === "completed" ? "bg-green-100 text-green-700" :
                              related.status === "ongoing" ? "bg-blue-100 text-blue-700" :
                              "bg-yellow-100 text-yellow-700"
                            }`}>
                              {getStatusText(related.status)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's work together to bring your engineering vision to life. 
            Our team of experts is ready to help you achieve your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacts"
              className="bg-white text-orange-500 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="wrench" size="sm" animated={false} />
              Get in Touch
            </Link>
            <Link
              to="/projects"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-500 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="gear" size="sm" />
              View All Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
